import {
  HomeTilesNavigationButtonConfiguration,
  InternalChargeCardActionLocationType,
  Maybe,
  Query,
  RouteNavigationAction,
} from '@varo/graphql-schema-types';
import { Action } from 'expo-quick-actions';
import { useFocusEffect, useLocalSearchParams } from 'expo-router';
import adapterMap from 'features/home/HomeScreen/adapters/adapterMap/adapterMap';
import mapHomeTiles from 'features/home/HomeScreen/adapters/mapHomeTiles/mapHomeTiles';
import mapSystemBanners from 'features/home/HomeScreen/adapters/mapSystemBanner/mapSystemBanners';
import { shouldHideBrazeForTile } from 'features/home/HomeScreen/adapters/onboardingProgressChecklistTile/onboardingChecklistTile';
import { BelievePaymentBottomSheetProps } from 'features/home/HomeScreen/components/BelievePaymentBottomSheet/BelievePaymentBottomSheet';
import { BrazeBottomSheetProps } from 'features/home/HomeScreen/components/BrazeBottomSheet/BrazeBottomSheet';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import useHomeCardDependencies from 'features/home/HomeScreen/hooks/useHomeCardDependencies/useHomeCardDependencies';
import { useUnsupportedBanner } from 'features/home/HomeScreen/hooks/useUnsupportedBanner/useUnsupportedBanner';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { requestContentCardsRefresh } from 'services/engagement/engagementManager';
import { FetchHomeTilesV2 } from 'services/graphql/queries/FetchHomeTilesV2';
import QueryMyAccountsBelieveCardAccount from 'services/graphql/queries/MyAccountsBelieveCardAccount.query';
import { ButtonProps } from 'shared/components/vxl/Button/Button';
import { SystemBannerProps } from 'shared/components/vxl/SystemBanner/SystemBanner.types';
import homeTileTypes from 'shared/constants/homeTileTypes';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useEngagement } from 'shared/hooks/useBraze';
import { VXLNode } from 'shared/renderer/types';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import buildOSQuickActions from 'shared/utils/osQuickActionUtils';
import { CombinedError, useQuery } from 'urql';

export interface NavigationButton {
  label?: string;
  onPress?: Maybe<RouteNavigationAction>;
}

export interface HomeScreenContent {
  displayBanner: SystemBannerProps;
  cards: VXLNode[];
  navigationButton: Maybe<ButtonProps>;
  appQuickActions: Action[];
}

export type HomeScreenNetworkState = 'loading' | 'refreshing' | null;

export interface HomeScreenData {
  fetching: HomeScreenNetworkState;
  error?: CombinedError;
  content?: HomeScreenContent | null;
  refresh: (useInlineLoading: boolean) => void;
  homeScreenBottomSheetContent:
    | BelievePaymentBottomSheetProps
    | BrazeBottomSheetProps
    | null;
  pinWheelModal?: ReactNode;
  pinWheelLoading?: boolean;
}

const makeNavigationButton = ({
  navigationButton,
  dependencies,
}: {
  navigationButton?: Maybe<HomeTilesNavigationButtonConfiguration>;
  dependencies: HomeCardDependencies;
}): Maybe<ButtonProps> => {
  if (!navigationButton) {
    return null;
  }

  const { onPress } = navigationButton ?? {};

  const onButtonPress: Maybe<() => void> = isNotNil(onPress)
    ? () => {
        dependencies.navigateToRoute(onPress);
      }
    : null;

  return {
    label: navigationButton?.label ?? '',
    size: 'SMALL',
    type: 'SECONDARY',
    width: 'HUG',
    onDark: true,
    onPress: onButtonPress,
  };
};

const useHomeScreenData = (): HomeScreenData => {
  const refreshingRef = useRef(false);
  const dependencies = useHomeCardDependencies();
  const [{ data, error, fetching }, refetch] = useQuery<Query>({
    query: FetchHomeTilesV2,
    variables: {
      tileTypes: homeTileTypes,
    },
    requestPolicy: 'cache-and-network',
  });
  const [{ data: believeCardData }] = useQuery<Query>({
    query: QueryMyAccountsBelieveCardAccount,
    variables: {},
    requestPolicy: 'cache-and-network',
  });

  const unsupportedBannerProps = useUnsupportedBanner();
  const { bottomSheetContent } = useEngagement();
  const { refreshTileId } = useLocalSearchParams();
  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_WELCOME,
  ]);
  const { refreshTiles: refreshDiscoverMoreTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_DISCOVER_MORE,
  ]);

  useEffect(() => {
    if (refreshTileId === HomeTileTypeIds.VXL_CARD_GROUP_TILE_WELCOME) {
      refreshTiles([refreshTileId]);
    }
  }, [refreshTileId, refreshTiles]);

  useFocusEffect(
    useCallback(() => {
      refreshDiscoverMoreTiles([
        HomeTileTypeIds.VXL_CARD_GROUP_TILE_DISCOVER_MORE,
      ]);
      refetch({ requestPolicy: 'cache-and-network' });
      requestContentCardsRefresh();
    }, [refreshDiscoverMoreTiles, refetch]),
  );
  const isRefreshing = refreshingRef.current;

  const loadingState = useMemo(() => {
    if (isRefreshing && fetching) {
      return 'refreshing';
    } else if (fetching) {
      return 'loading';
    }
    return null;
  }, [isRefreshing, fetching]);

  const believeBottomSheetContent = useMemo(() => {
    if (!believeCardData) {
      return null;
    }
    const { myAccounts } = believeCardData;
    if (!myAccounts) {
      return null;
    }

    const { id: accountId, action: actionContent } =
      myAccounts.chargeCard ?? {};
    if (
      accountId &&
      actionContent &&
      actionContent.location.includes(
        InternalChargeCardActionLocationType.LoginAction,
      )
    ) {
      return {
        bottomSheetRef: null,
        id: accountId,
        data: actionContent,
      };
    }
    return null;
  }, [believeCardData]);

  const content = useMemo<HomeScreenContent | null>(() => {
    if (!data) {
      return null;
    }

    const { homeTilesV2 } = data;
    if (homeTilesV2) {
      const cards = mapHomeTiles(
        adapterMap,
        homeTilesV2.tiles ?? [],
        dependencies,
      );

      const quickActions = buildOSQuickActions(homeTilesV2.tiles ?? []);
      const banners = mapSystemBanners(
        homeTilesV2.systemBanners ?? [],
        dependencies,
      );

      const displayBanner = isNotNil(unsupportedBannerProps)
        ? [unsupportedBannerProps, ...banners][0]
        : (banners[0] ?? null);

      const { navigationButton } = homeTilesV2;

      return {
        displayBanner,
        cards,
        navigationButton: makeNavigationButton({
          navigationButton,
          dependencies,
        }),
        appQuickActions: quickActions,
      };
    }

    return null;
  }, [data, dependencies, unsupportedBannerProps]);

  // Control Braze card visibility based on presence of onboarding checklist tile.
  useEffect(() => {
    const tiles = data?.homeTilesV2?.tiles ?? [];
    const hasOnboardingChecklist = tiles.some((t) => shouldHideBrazeForTile(t));

    if (typeof dependencies?.setBrazeCardsVisible === 'function') {
      dependencies.setBrazeCardsVisible(!hasOnboardingChecklist);
    }
  }, [data?.homeTilesV2?.tiles, dependencies]);

  const refresh = useCallback(() => {
    refreshingRef.current = true;
    refetch({ requestPolicy: 'network-only' });
  }, [refetch]);

  return {
    fetching: loadingState,
    content,
    error: error,
    refresh,
    homeScreenBottomSheetContent:
      believeBottomSheetContent ?? bottomSheetContent ?? null,
  };
};

export default useHomeScreenData;
